import React from "react";
import { Button, Popconfirm, Table, Tag, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import AppLayout from "../components/Layout";
import DataTable from "../components/DataTable";
import SearchBox from "../components/SearchBox";
import usePagedList from "../lib/usePagedList";
import httpClient from "../axios";
import { formatDateTime, notifyError, notifySuccess } from "../helper";

/** The headsets send dates as "2026.10.27-11.33.48"; show them in a readable form. */
const formatStepDate = (value) => {
	if (!value) return "-";
	const m = /^(\d{4})\.(\d{2})\.(\d{2})-(\d{2})\.(\d{2})\.(\d{2})$/.exec(value);
	if (m) return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]}`;
	return formatDateTime(value);
};

const steps = (scenarios) => (Array.isArray(scenarios) ? scenarios : Object.values(scenarios || {})).filter(Boolean);

const ResultTag = ({ result }) => {
	const value = String(result || "").toLowerCase();
	if (value === "pass" || value === "passed" || value === "true") return <Tag color="green">Pass</Tag>;
	if (value === "fail" || value === "failed" || value === "false") return <Tag color="red">Fail</Tag>;
	return <Tag>{result || "-"}</Tag>;
};

const Results = () => {
	const http = React.useMemo(() => httpClient(), []);
	const list = usePagedList("results");
	const [deleting, setDeleting] = React.useState(null);

	const remove = (id) => {
		setDeleting(id);
		http
			.delete(`/results/${id}`)
			.then(() => {
				notifySuccess("Result deleted");
				list.reload();
			})
			.catch((error) => notifyError(error, "Could not delete the result"))
			.finally(() => setDeleting(null));
	};

	const columns = [
		{
			title: "Contact",
			key: "student",
			render: (row) =>
				row.student ? (
					<>
						<div style={{ fontWeight: 600 }}>{row.student.name}</div>
						{row.student.email && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.student.email}</Typography.Text>}
					</>
				) : (
					<Typography.Text type="secondary">Unknown contact</Typography.Text>
				),
		},
		{ title: "Scenario", key: "module", render: (row) => (row.module ? row.module.name : "-") },
		{
			title: "Steps",
			key: "steps",
			align: "center",
			render: (row) => {
				const all = steps(row.scenarios);
				const passed = all.filter((s) => ["pass", "passed", "true"].includes(String(s.result).toLowerCase())).length;
				return (
					<span>
						<strong>{passed}</strong> / {all.length} passed
					</span>
				);
			},
		},
		{ title: "Submitted", dataIndex: "created_at", key: "created_at", render: formatDateTime },
		{
			title: "",
			key: "actions",
			align: "right",
			render: (row) => (
				<Popconfirm title="Delete this result permanently?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(row.id)}>
					<Button danger size="small" icon={<DeleteOutlined />} loading={deleting === row.id}>
						Delete
					</Button>
				</Popconfirm>
			),
		},
	];

	const expanded = (row) => (
		<Table
			size="small"
			pagination={false}
			rowKey={(s, i) => `${row.id}-${i}`}
			dataSource={steps(row.scenarios)}
			columns={[
				{ title: "Step", dataIndex: "name", key: "name" },
				{ title: "Completed", dataIndex: "completion_date", key: "completion_date", width: 200, render: formatStepDate },
				{ title: "Result", dataIndex: "result", key: "result", width: 100, render: (v) => <ResultTag result={v} /> },
			]}
		/>
	);

	return (
		<AppLayout
			title="Training results"
			subtitle="Results posted by the VR scenarios. Expand a row to see each step."
			actions={<SearchBox onSearch={list.onSearch} placeholder="Search contact or scenario" />}
		>
			<DataTable
				rows={list.rows}
				columns={columns}
				loading={list.loading}
				pagination={list.pagination}
				expandable={{ expandedRowRender: expanded, rowExpandable: (row) => steps(row.scenarios).length > 0 }}
			/>
		</AppLayout>
	);
};

export default Results;
